import NumberFlow from "@number-flow/react";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  UseFieldArrayRemove,
  UseFormReturn,
  useFieldArray,
  useFormContext,
} from "react-hook-form";

import FormInput from "@/components/form-fields/form-input";
import { FileUploaderComponent } from "@/components/reusables/FileUpload";
import { FormSelectSingle } from "@/components/reusables/custom-form-fields/FormSelect";
import { TimePicker12 } from "@/components/reusables/time-picker/time-picker-12hour";
import { Period } from "@/components/reusables/time-picker/time-picker-utils";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import * as Table from "@/components/ui/table";
import { allowedImageTypes, dayNames } from "@/constants/constants";
import { IDays } from "@/interfaces/interfaces";
import { ISubContractor } from "@/interfaces/subcontractor.interface";
import { Holiday, IBreak, TimeShift } from "@/interfaces/timeTracking";
import { cn } from "@/lib/utils";
import {
  useGetShiftsQuery,
  useGetTimeSettingDataQuery,
} from "@/redux/api/timeTrackingSettingQuery";
import { useGetUsersByWSQuery } from "@/redux/api/userQuery";
import { TDailyReport } from "@/schemas/daily-report.schema";
import {
  calculateAllowedBreak,
  calculateTotalUserHours,
  dateToSec,
  getMainBreakDown,
  secToDate,
  secToDateFormat,
  sortShifts,
} from "@/utils/helper-functions/date-time.helper";

export const SingleSubContractorWH = ({
  index,
  field,
  remove,
  subcontractors,
  isLoading,
  form,
  shifts,
  breakList,
  holiday,
}: {
  index: number;
  field: Record<"arrayId", string>;
  remove: UseFieldArrayRemove;
  subcontractors: ISubContractor[];
  isLoading: boolean;
  form: UseFormReturn<TDailyReport>;
  shifts: TimeShift[];
  breakList: IBreak[];
  holiday?: Holiday;
}): React.JSX.Element => {
  const startDate = secToDate(
    +form.getValues(`subcontractorWorkingHours.${index}.startTime`),
  );
  const endDate = secToDate(
    +form.getValues(`subcontractorWorkingHours.${index}.endTime`),
  );

  const [startTime, setStartTime] = useState<Date | undefined>(
    new Date(startDate ?? new Date().setHours(0, 0, 0, 0)),
  );
  const [endTime, setEndTime] = useState<Date | undefined>(
    new Date(endDate ?? new Date().setHours(0, 0, 0, 0)),
  );

  const [startPeriod, setStartPeriod] = useState<Period>(
    dateToSec(startTime) > 43199 ? "PM" : "AM",
  );
  const [endPeriod, setEndPeriod] = useState<Period>(
    dateToSec(endTime) > 43199 ? "PM" : "AM",
  );

  const dayDate = new Date(form.getValues("date") ?? "");
  const day = dayNames[dayDate.getDay()] as IDays;

  const handleTime = (date: Date): number => {
    if (startTime === endTime) {
      const tempDate = new Date(new Date().setHours(0, 0, 0, 0));

      return dateToSec(tempDate);
    }

    return dateToSec(date);
  };
  const breakDowns = getMainBreakDown({
    startTime: dateToSec(startTime),
    endTime: dateToSec(endTime),
    shifts,
    day,
    holiday,
  });

  const allowedBreak = calculateAllowedBreak(
    dateToSec(startTime),
    dateToSec(endTime),
    breakList,
  );

  const handleWhDelete = (index: number, id: string | undefined): void => {
    if (typeof id === "undefined") {
      remove(index);

      return;
    }
    const currentDeletedList =
      form.getValues("deleteSubcontractorWorkingHours") || [];
    const newDeletedList = [...currentDeletedList, id];

    form.setValue(`deleteSubcontractorWorkingHours`, newDeletedList, {
      shouldDirty: true,
    });
    remove(index);
  };

  useEffect(() => {
    form.setValue(`subcontractorWorkingHours.${index}.break`, allowedBreak, {
      shouldDirty: true,
    });
    form.setValue(
      `subcontractorWorkingHours.${index}.workBreakDown.${0}.time`,
      +breakDowns
        ?.filter((b) => b.title === "Normal Hours")
        .reduce((acc, b) => acc + b.time, 0) || 0,
      { shouldDirty: true },
    );
    form.setValue(
      `subcontractorWorkingHours.${index}.workBreakDown.${1}.time`,
      +breakDowns
        ?.filter((b) => b.title === "Overnight Hours")
        .reduce((acc, b) => acc + b.time, 0) || 0,
      { shouldDirty: true },
    );
    form.setValue(
      `subcontractorWorkingHours.${index}.workBreakDown.${2}.time`,
      +breakDowns
        ?.filter((b) => b.title === "Weekend")
        .reduce((acc, b) => acc + b.time, 0) || 0,
    );

    form.setValue(
      `subcontractorWorkingHours.${index}.totalHours`,
      +calculateTotalUserHours(startTime!, endTime!).toFixed(2),
    );
  }, [startTime, endTime, day, startPeriod, endPeriod]);

  const commonCellClassName = "bg-background hover:bg-background";

  return (
    <Table.TableRow key={field.arrayId}>
      <Table.TableCell className={cn(commonCellClassName)}>
        {index + 1}
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName, "w-[300px]")}>
        <FormInput
          name={`subcontractorWorkingHours.${index}.subcontractorId`}
          form={form}
          render={(field) => (
            <FormSelectSingle
              field={field}
              options={
                Array.isArray(subcontractors) && subcontractors?.length
                  ? subcontractors?.map((subcontactor: ISubContractor) => ({
                      label: subcontactor?.name || "",
                      value: subcontactor?.id,
                    }))
                  : []
              }
              isLoading={isLoading}
              placeholder='Select Subcontractor'
            />
          )}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName)}>
        <FormInput
          name={`subcontractorWorkingHours.${index}.startTime`}
          form={form}
          render={(field) => (
            <TimePicker12
              disabled={!day}
              date={startTime}
              setDate={(date) => {
                field.onChange(dateToSec(date!));
                setStartTime(date);
              }}
              AMPM={startPeriod}
              setAMPM={setStartPeriod}
            />
          )}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName)}>
        <FormInput
          name={`subcontractorWorkingHours.${index}.endTime`}
          form={form}
          render={(field) => (
            <TimePicker12
              disabled={!day}
              date={endTime}
              setDate={(date) => {
                field.onChange(handleTime(date!));
                setEndTime(date);
              }}
              AMPM={endPeriod}
              setAMPM={setEndPeriod}
            />
          )}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName, "w-[130px]")}>
        <FormInput
          name={`subcontractorWorkingHours.${index}.workBreakDown.${0}.time`}
          form={form}
          render={() => {
            const displayValue = secToDateFormat(
              form.getValues(
                `subcontractorWorkingHours.${index}.workBreakDown.${0}.time`,
              ),
            );

            return <Input type='text' disabled value={displayValue} />;
          }}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName, "w-[130px]")}>
        <FormInput
          name={`subcontractorWorkingHours.${index}.workBreakDown.${1}.time`}
          form={form}
          render={() => {
            const displayValue = secToDateFormat(
              form.getValues(
                `subcontractorWorkingHours.${index}.workBreakDown.${1}.time`,
              ),
            );

            return <Input type='text' disabled value={displayValue} />;
          }}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName, "w-[130px]")}>
        <FormInput
          name={`subcontractorWorkingHours.${index}.workBreakDown.${2}.time`}
          form={form}
          render={() => {
            const displayValue = secToDateFormat(
              form.getValues(
                `subcontractorWorkingHours.${index}.workBreakDown.${2}.time`,
              ),
            );

            return <Input type='text' disabled value={displayValue} />;
          }}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName, "w-[120px]")}>
        <FormInput
          name={`subcontractorWorkingHours.${index}.break`}
          form={form}
          input={{
            type: "number",
          }}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName, "w-[120px]")}>
        <p className='text-center text-aldgray'>
          <NumberFlow
            value={form.watch(`subcontractorWorkingHours.${index}.totalHours`)}
          />
        </p>
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName, "w-[300px]")}>
        <FormInput
          name={`subcontractorWorkingHours.${index}.files`}
          form={form}
          render={(field, _, formState) => (
            <FileUploaderComponent
              field={field}
              formState={formState}
              isMultiple
              maxFilePreview={2}
              isForFileUpload={true}
              acceptFileTypes={allowedImageTypes}
              {...field}
            />
          )}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName, "w-[280px]")}>
        <FormInput
          name={`subcontractorWorkingHours.${index}.note`}
          form={form}
          input={{
            placeholder: "Notes",
          }}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName, "px-2")}>
        <Trash2
          size={20}
          color='red'
          onClick={() =>
            handleWhDelete(
              index,
              form.getValues(`subcontractorWorkingHours.${index}.id`),
            )
          }
        />
      </Table.TableCell>
    </Table.TableRow>
  );
};

const SubcontractorWH = ({
  holiday,
}: {
  holiday: Holiday | undefined;
}): React.JSX.Element => {
  const form = useFormContext<TDailyReport>();
  const { control } = form;

  const { data: shifts, isLoading: isSubcontractorLoading } =
    useGetShiftsQuery("SUBCONTRACTOR");

  const { data: breakList } = useGetTimeSettingDataQuery({
    type: "break",
    filters: "",
  });

  const [defaultBreakDown, setDefaultBreakDown] = useState<
    {
      shiftId: string;
      time: number;
    }[]
  >();

  const {
    fields: SubcontractorField,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "subcontractorWorkingHours",
    keyName: "arrayId",
  });

  const { data: subcontractors, isLoading } = useGetUsersByWSQuery({
    userType: "subcontractor",
    status: "ACTIVE",
  });

  const addEmployee = (): void => {
    append({
      subcontractorId: "",
      startTime: 0,
      endTime: 0,
      workBreakDown: defaultBreakDown || [],
      break: 0,
      totalHours: 0,
    });
  };
  const { theme: skin } = useTheme();

  // set the default workbreakdown with id and time according to the shifts in settings
  useEffect(() => {
    if (!isSubcontractorLoading && shifts) {
      const reformat = sortShifts(shifts);
      const sortedShifts = reformat?.[0];
      const breakDowns = sortedShifts.map((shift: TimeShift) => ({
        shiftId: shift.id,
        time: 0,
      }));
      setDefaultBreakDown(breakDowns);
    }
  }, [isSubcontractorLoading, shifts]);

  return (
    <div className='my-8'>
      <h2 className='mb-4 text-lg font-medium'>Subcontractor Working Hours</h2>
      <div className='border rounded-sm '>
        <Table.Table>
          <Table.TableHeader>
            <Table.TableRow
              className={cn(
                skin !== "dark" && "bg-[#E2EBF5] hover:bg-secondary ",
              )}
            >
              <Table.TableHead className='px-2'>S.N</Table.TableHead>
              <Table.TableHead className='min-w-[250px]'>
                SUBCONTRACTOR NAME
              </Table.TableHead>
              <Table.TableHead>START TIME</Table.TableHead>
              <Table.TableHead>END TIME</Table.TableHead>
              <Table.TableHead className='min-w-[110px]'>
                NORMAL{" "}
              </Table.TableHead>
              <Table.TableHead className='min-w-[110px]'>
                OVERTIME{" "}
              </Table.TableHead>
              <Table.TableHead className='min-w-[110px]'>
                WEEKEND{" "}
              </Table.TableHead>
              <Table.TableHead className='min-w-[80px]'>BREAK </Table.TableHead>
              <Table.TableHead>TOTAL </Table.TableHead>
              <Table.TableHead>IMAGE </Table.TableHead>
              <Table.TableHead className='min-w-[200px]'>
                NOTES{" "}
              </Table.TableHead>
              <Table.TableHead> </Table.TableHead>
            </Table.TableRow>
          </Table.TableHeader>
          <Table.TableBody>
            {SubcontractorField.map((field, index) => (
              <SingleSubContractorWH
                key={field.arrayId}
                index={index}
                field={field}
                remove={remove}
                subcontractors={subcontractors?.data as ISubContractor[]}
                isLoading={isLoading}
                form={form}
                shifts={shifts || []}
                breakList={(breakList?.data as IBreak[]) || []}
                holiday={holiday}
              />
            ))}
            <Table.TableRow className='!bg-background dark:bg-primary'>
              <Table.TableCell colSpan={11} className='text-left'>
                <Button
                  type='button'
                  onClick={() => addEmployee()}
                  variant={"light"}
                  className={cn(SubcontractorField.length === 0 && "mt-4")}
                >
                  + Add New Row
                </Button>
              </Table.TableCell>
            </Table.TableRow>
          </Table.TableBody>
        </Table.Table>
      </div>
    </div>
  );
};

export default SubcontractorWH;
